import React, { useState } from 'react';
import { ScrubInput } from 'calc-controls';
import { useAppStore } from '@/store';
import type { MeetingPattern, SessionType, GradingScaleEntry } from '@abs/shared-types';
import { SessionType as ST } from '@abs/shared-types';
import { Button, Input, DatePicker, Select } from '../shared';
import type { SelectOption } from '../shared';

const DAY_OPTIONS: SelectOption[] = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const SESSION_TYPE_OPTIONS: SelectOption[] = Object.values(ST).map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' '),
}));

const DEFAULT_GRADING_SCALE: GradingScaleEntry[] = [
  { letter: 'A', minPercentage: 93 },
  { letter: 'A−', minPercentage: 90 },
  { letter: 'B+', minPercentage: 87 },
  { letter: 'B', minPercentage: 83 },
  { letter: 'B−', minPercentage: 80 },
  { letter: 'C+', minPercentage: 77 },
  { letter: 'C', minPercentage: 73 },
  { letter: 'C−', minPercentage: 70 },
  { letter: 'D+', minPercentage: 67 },
  { letter: 'D', minPercentage: 63 },
  { letter: 'F', minPercentage: 0 },
];

export interface CourseSetupFormProps {
  onSave: (courseId: string) => void;
  onCancel: () => void;
}

export function CourseSetupForm({ onSave, onCancel }: CourseSetupFormProps): React.ReactElement {
  const addCourse = useAppStore((s) => s.addCourse);
  const addTerm = useAppStore((s) => s.addTerm);
  const policies = useAppStore((s) => s.policies);
  const terms = useAppStore((s) => s.terms);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [instructor, setInstructor] = useState('');
  const [termName, setTermName] = useState('');
  const [termStart, setTermStart] = useState('');
  const [termEnd, setTermEnd] = useState('');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [patterns, setPatterns] = useState<MeetingPattern[]>([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [baseGrade, setBaseGrade] = useState<number | null>(null);
  const [gradingScale] = useState<GradingScaleEntry[]>(DEFAULT_GRADING_SCALE);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Create term if needed
    let termId = selectedTermId;
    if (!termId && termName && termStart && termEnd) {
      termId = addTerm({ name: termName, startDate: termStart, endDate: termEnd, isArchived: false });
    }

    const courseId = addCourse({
      name,
      code,
      instructor,
      termId: termId ?? '',
      policyId: selectedPolicyId,
      meetingPatterns: patterns,
      sessionOverrides: [],
      totalSessions: 0,
      creditHours: 3,
      startDate: termStart,
      endDate: termEnd,
      baseGrade,
      gradingScale,
    });

    onSave(courseId);
  };

  const addPattern = () => {
    setPatterns([...patterns, {
      dayOfWeek: 1,
      startTime: '09:00',
      endTime: '10:15',
      sessionType: ST.ClassMeeting,
    }]);
  };

  const updatePattern = (index: number, updates: Partial<MeetingPattern>) => {
    setPatterns(patterns.map((p, i) => (i === index ? { ...p, ...updates } : p)));
  };

  const removePattern = (index: number) => {
    setPatterns(patterns.filter((_, i) => i !== index));
  };

  return (
    <form className="course-setup-form" onSubmit={handleSubmit}>
      <Input label="Course Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <Input label="Course Code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CS 101" />
      <Input label="Instructor" value={instructor} onChange={(e) => setInstructor(e.target.value)} />

      <fieldset className="course-setup-form__fieldset">
        <legend>Term</legend>
        {terms.length > 0 && (
          <Select
            label="Existing Term"
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
            value={selectedTermId}
            onChange={(e) => setSelectedTermId(e.target.value)}
            placeholder="— Create new —"
          />
        )}
        {!selectedTermId && (
          <>
            <Input label="Term Name" value={termName} onChange={(e) => setTermName(e.target.value)} placeholder="Fall 2026" />
            <DatePicker label="Start Date" value={termStart} onChange={(e) => setTermStart(e.target.value)} />
            <DatePicker label="End Date" value={termEnd} onChange={(e) => setTermEnd(e.target.value)} />
          </>
        )}
      </fieldset>

      <fieldset className="course-setup-form__fieldset">
        <legend>Meeting Schedule</legend>
        {patterns.map((p, i) => (
          <div key={i} className="course-setup-form__pattern">
            <Select
              label="Day"
              options={DAY_OPTIONS}
              value={String(p.dayOfWeek)}
              onChange={(e) => updatePattern(i, { dayOfWeek: Number(e.target.value) as MeetingPattern['dayOfWeek'] })}
            />
            <Input label="Start" type="time" value={p.startTime} onChange={(e) => updatePattern(i, { startTime: e.target.value })} />
            <Input label="End" type="time" value={p.endTime} onChange={(e) => updatePattern(i, { endTime: e.target.value })} />
            <Select
              label="Type"
              options={SESSION_TYPE_OPTIONS}
              value={p.sessionType}
              onChange={(e) => updatePattern(i, { sessionType: e.target.value as SessionType })}
            />
            <Button variant="danger" size="sm" onClick={() => removePattern(i)}>Remove</Button>
          </div>
        ))}
        <Button variant="secondary" size="sm" onClick={addPattern}>+ Add Meeting</Button>
      </fieldset>

      <Select
        label="Attendance Policy"
        options={policies.map((p) => ({ value: p.id, label: p.name }))}
        value={selectedPolicyId}
        onChange={(e) => setSelectedPolicyId(e.target.value)}
        placeholder="— Select policy —"
        hint="The absence policy that determines penalties, the auto-fail threshold, and grace allowances for this course."
      />

      <div className="course-setup-form__base-grade">
        <label className="course-setup-form__check">
          <input
            type="checkbox"
            checked={baseGrade !== null}
            onChange={(e) => setBaseGrade(e.target.checked ? 0 : null)}
          />
          Set a base grade override
        </label>
        {baseGrade !== null && (
          <ScrubInput
            label="Base Grade"
            value={baseGrade}
            onChange={setBaseGrade}
            min={0}
            max={100}
            decimals={0}
            tooltip="Current course grade (0–100%) before attendance penalties are applied."
          />
        )}
      </div>

      <div className="course-setup-form__actions">
        <Button type="submit" variant="primary">Create Course</Button>
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
